/**
 * The Tasks filter bar (`docs/13-tasks-section-proposal.md` §2: *"workspace, board, status,
 * assignee/assigner, due range, done/open, text search; sort; saved personal views"*).
 *
 * **Where the options come from.** `tasks.facets` (round T8, P-274): the tab's **unfiltered**
 * row set, behind the same permission rule as the list, so every option offered is one the
 * person can really see and no board, person or label is disclosed that their own unfiltered
 * list would not already contain. They used to be derived from the rows on screen — which are
 * the rows the filters already narrowed — so picking board A emptied the picker of board B,
 * the person picker lost everybody that board excluded, a board further down than the first
 * page was unreachable, and the workspace picker could not exist at all, because a row does not
 * carry its workspace. `filterOptionsFrom` survives as the fallback for a source that has no
 * facets yet (the fixture harness).
 *
 * On a phone the same component renders as a **bottom sheet** (`mobile.css`, F9-D14): one
 * implementation, one set of test ids, so a filter that works on the desktop cannot be missing
 * on the phone.
 */
import { useMemo } from 'react';
import {
  EMPTY_TASK_FILTERS,
  activeFilterCount,
  filtersAreEmpty,
  type TaskFilterState,
} from './state';
import {
  TASK_ASSIGNED_RANGES,
  TASK_ASSIGNED_RANGE_LABELS,
  TASK_DONE_FILTER_LABELS,
  TASK_DUE_RANGES,
  TASK_DUE_RANGE_LABELS,
  type TaskAssignedRange,
  type TaskDueRange,
} from './vocabulary';
import type { TaskDoneFilter, TaskFacetsView, TaskRowView, TaskTab } from './types';

export interface TaskFilterOptions {
  workspaces: { id: string; name: string }[];
  boards: { id: string; name: string; workspaceId: string }[];
  people: { id: string; name: string }[];
  statusLabels: string[];
}

/** `tasks.facets` reduced to what the bar draws: one person list, whichever side the tab is about. */
export function filterOptionsFromFacets(facets: TaskFacetsView, tab: TaskTab): TaskFilterOptions {
  return {
    workspaces: facets.workspaces,
    boards: facets.boards,
    people: tab === 'assigned' ? facets.assignees : facets.assigners,
    statusLabels: facets.statusLabels,
  };
}

/** The pickers' contents, derived from one page of rows. Sorted by name, deduplicated by id. */
export function filterOptionsFrom(rows: readonly TaskRowView[], tab: TaskTab): TaskFilterOptions {
  const boards = new Map<string, string>();
  const people = new Map<string, string>();
  const statusLabels = new Set<string>();
  for (const row of rows) {
    boards.set(row.boardId, row.boardName);
    // My Tasks filters by **assigner** ("who assigns me the most"); the delegation tabs by
    // assignee. One picker, whichever side of the pair this tab is about.
    const person = tab === 'assigned' ? row.assignee : row.assigner;
    if (person) people.set(person.id, person.name ?? person.id);
    if (row.statusLabel) statusLabels.add(row.statusLabel);
  }
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return {
    workspaces: [],
    boards: [...boards.entries()].map(([id, name]) => ({ id, name, workspaceId: '' })).sort(byName),
    people: [...people.entries()].map(([id, name]) => ({ id, name })).sort(byName),
    statusLabels: [...statusLabels].sort((a, b) => a.localeCompare(b)),
  };
}

export interface TaskFiltersProps {
  tab: TaskTab;
  filters: TaskFilterState;
  rows: readonly TaskRowView[];
  /** `tasks.facets`; absent while it is in flight, and the rows on screen stand in for it. */
  facets?: TaskFacetsView | null;
  onChange(next: TaskFilterState): void;
  /** The phone's bottom sheet renders a title row and a `Done` button; the desktop bar does not. */
  asSheet?: boolean;
  onClose?(): void;
  /** Saves the current `(tab, groupBy, sort, filters)` under a name. */
  onSaveView?(): void;
}

/** A `<select multiple>` is monday-ugly; the bar uses one-per-picker single selects plus chips. */
function single(values: string[]): string {
  return values.length === 1 ? (values[0] as string) : '';
}

export function TaskFilters({
  tab,
  filters,
  rows,
  facets = null,
  onChange,
  asSheet = false,
  onClose,
  onSaveView,
}: TaskFiltersProps) {
  const options = useMemo(
    () => (facets ? filterOptionsFromFacets(facets, tab) : filterOptionsFrom(rows, tab)),
    [facets, rows, tab],
  );
  /*
   * Picking a workspace narrows the **board picker** as well as the query, which is what the
   * pair means on monday; a board already picked in another workspace is cleared with it, so the
   * two can never contradict each other into an empty list.
   */
  const boardsForWorkspace =
    filters.workspaceIds.length === 0
      ? options.boards
      : options.boards.filter((board) => filters.workspaceIds.includes(board.workspaceId));
  const count = activeFilterCount(filters);

  return (
    <div
      className="ez-tasks-filters"
      data-sheet={asSheet || undefined}
      role={asSheet ? 'dialog' : undefined}
      aria-label={asSheet ? 'Filter tasks' : undefined}
      data-testid="tasks-filters"
    >
      {asSheet ? (
        <div className="ez-tasks-filters__sheet-head">
          <span className="ez-tasks-filters__sheet-title">Filter</span>
          <button type="button" data-testid="tasks-filters-done" onClick={() => onClose?.()}>
            Done
          </button>
        </div>
      ) : null}

      {options.workspaces.length > 0 ? (
        <label className="ez-tasks-filters__field">
          <span>Workspace</span>
          <select
            data-testid="tasks-filter-workspace"
            value={single(filters.workspaceIds)}
            onChange={(event) => {
              const ids = event.target.value === '' ? [] : [event.target.value];
              const keep = filters.boardIds.filter((id) =>
                ids.length === 0
                  ? true
                  : options.boards.some((board) => board.id === id && ids.includes(board.workspaceId)),
              );
              onChange({ ...filters, workspaceIds: ids, boardIds: keep });
            }}
          >
            <option value="">All workspaces</option>
            {options.workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="ez-tasks-filters__field">
        <span>Board</span>
        <select
          data-testid="tasks-filter-board"
          value={single(filters.boardIds)}
          onChange={(event) =>
            onChange({ ...filters, boardIds: event.target.value === '' ? [] : [event.target.value] })
          }
        >
          <option value="">All boards</option>
          {boardsForWorkspace.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name}
            </option>
          ))}
        </select>
      </label>

      <label className="ez-tasks-filters__field">
        <span>{tab === 'assigned' ? 'Assignee' : 'Assigned by'}</span>
        <select
          data-testid="tasks-filter-person"
          value={single(tab === 'assigned' ? filters.assigneeIds : filters.assignerIds)}
          onChange={(event) => {
            const ids = event.target.value === '' ? [] : [event.target.value];
            onChange(
              tab === 'assigned'
                ? { ...filters, assigneeIds: ids }
                : { ...filters, assignerIds: ids },
            );
          }}
        >
          <option value="">Anyone</option>
          {options.people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name}
            </option>
          ))}
        </select>
      </label>

      <label className="ez-tasks-filters__field">
        <span>Status</span>
        <select
          data-testid="tasks-filter-status"
          value={single(filters.statusLabels)}
          onChange={(event) =>
            onChange({
              ...filters,
              statusLabels: event.target.value === '' ? [] : [event.target.value],
            })
          }
        >
          <option value="">Any status</option>
          {options.statusLabels.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="ez-tasks-filters__field">
        <span>Due</span>
        <select
          data-testid="tasks-filter-due"
          value={filters.dueRange}
          onChange={(event) => onChange({ ...filters, dueRange: event.target.value as TaskDueRange })}
        >
          {TASK_DUE_RANGES.map((range) => (
            <option key={range} value={range}>
              {TASK_DUE_RANGE_LABELS[range]}
            </option>
          ))}
        </select>
      </label>

      {/*
        P-301 · the two ends of `Custom…`, and they are drawn **only** when it is selected.
        A pair of date fields standing open beside every preset is two more controls in a
        390 px sheet that nobody is using, and the preset already says what the window is.
        Either side may be left empty (`state.ts`), which is why they are two independent
        fields and not one range control that insists on both.
      */}
      {filters.dueRange === 'custom' ? (
        <TaskRangeFields
          name="due"
          from={filters.dueFrom}
          to={filters.dueTo}
          onFrom={(value) => onChange({ ...filters, dueFrom: value })}
          onTo={(value) => onChange({ ...filters, dueTo: value })}
        />
      ) : null}

      <label className="ez-tasks-filters__field">
        <span>Assigned on</span>
        <select
          data-testid="tasks-filter-assigned"
          value={filters.assignedRange}
          onChange={(event) =>
            onChange({ ...filters, assignedRange: event.target.value as TaskAssignedRange })
          }
        >
          {TASK_ASSIGNED_RANGES.map((range) => (
            <option key={range} value={range}>
              {TASK_ASSIGNED_RANGE_LABELS[range]}
            </option>
          ))}
        </select>
      </label>

      {filters.assignedRange === 'custom' ? (
        <TaskRangeFields
          name="assigned"
          from={filters.assignedFrom}
          to={filters.assignedTo}
          onFrom={(value) => onChange({ ...filters, assignedFrom: value })}
          onTo={(value) => onChange({ ...filters, assignedTo: value })}
        />
      ) : null}

      <label className="ez-tasks-filters__field">
        <span>Show</span>
        <select
          data-testid="tasks-filter-done"
          value={filters.done}
          onChange={(event) => onChange({ ...filters, done: event.target.value as TaskDoneFilter })}
        >
          {(['any', 'open', 'done'] as const).map((value) => (
            <option key={value} value={value}>
              {TASK_DONE_FILTER_LABELS[value]}
            </option>
          ))}
        </select>
      </label>

      {/*
        The Archive tab *is* the archive, so the toggle would be a no-op there; every other tab
        carries it (`docs/13` §2: *"Show archived toggle in each tab"*).
      */}
      {tab === 'archive' ? null : (
        <label className="ez-tasks-filters__toggle">
          <input
            type="checkbox"
            data-testid="tasks-filter-show-archived"
            checked={filters.includeArchived}
            onChange={(event) => onChange({ ...filters, includeArchived: event.target.checked })}
          />
          Show archived
        </label>
      )}

      <div className="ez-tasks-filters__actions">
        <button
          type="button"
          className="ez-tasks-filters__clear"
          data-testid="tasks-filter-clear"
          disabled={filtersAreEmpty(filters)}
          onClick={() => onChange({ ...EMPTY_TASK_FILTERS })}
        >
          {count > 0 ? `Clear all (${count})` : 'Clear all'}
        </button>
        {onSaveView ? (
          <button
            type="button"
            className="ez-tasks-filters__save"
            data-testid="tasks-filter-save-view"
            onClick={onSaveView}
          >
            Save as a view
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The **From / To** pair a `Custom…` range opens — one component for both ranges (P-301 §1).
 *
 * `<input type="date">` and not a bespoke calendar: it is the control every phone already draws
 * its own native picker for, which is what §4 of the card means by *"native date inputs"* — and
 * on iOS an input whose font is below 16 px makes the page zoom on focus, so `mobile.css` sets
 * exactly 16 px on these two and the verifier measures it.
 *
 * Neither field is required and clearing one is a real gesture ("everything from the 1st,
 * whenever it ends"), so the empty string is passed straight through rather than being treated
 * as a failure to fill the form in.
 */
function TaskRangeFields({
  name,
  from,
  to,
  onFrom,
  onTo,
}: {
  name: 'due' | 'assigned';
  from: string;
  to: string;
  onFrom(value: string): void;
  onTo(value: string): void;
}) {
  return (
    <div className="ez-tasks-filters__range" data-testid={`tasks-filter-${name}-range`}>
      <label className="ez-tasks-filters__field ez-tasks-filters__field--date">
        <span>From</span>
        <input
          type="date"
          data-testid={`tasks-filter-${name}-from`}
          value={from}
          onChange={(event) => onFrom(event.target.value)}
        />
      </label>
      <label className="ez-tasks-filters__field ez-tasks-filters__field--date">
        <span>To</span>
        <input
          type="date"
          data-testid={`tasks-filter-${name}-to`}
          value={to}
          onChange={(event) => onTo(event.target.value)}
        />
      </label>
    </div>
  );
}
